import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface BreadthData {
  bias: string
  bullish: number
  bearish: number
  conflicting: number
  monitor: number
}

interface StockSignal {
  symbol: string
  score: number
  verdict: string
  conviction: string
  spot: number
  pcr: number
  reason: string
}

interface MarketOverview {
  scan_time?: string
  total_signals?: number
  breadth?: BreadthData
  institutional_buying?: StockSignal[]
  institutional_selling?: StockSignal[]
  status?: string
  message?: string
}

export default function Market() {
  const [data, setData] = useState<MarketOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState('')
  const navigate = useNavigate()

  const fetchData = async () => {
    try {
      const res = await api.get('/api/signals/market-overview')
      setData(res.data)
      setLastUpdate(new Date().toLocaleTimeString('en-IN'))
    } catch (err) {
      console.error('Failed to fetch market overview', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const biasColor = (bias: string) => {
    if (bias === 'BULLISH') return '#22c55e'
    if (bias === 'BEARISH') return '#ef4444'
    return '#f59e0b'
  }

  const verdictColor = (verdict: string) => {
    if (verdict === 'CONTEXT_BULLISH' || verdict === 'CONTEXT_BEARISH') return '#94a3b8'
    if (verdict.includes('BULLISH')) return '#22c55e'
    if (verdict.includes('BEARISH')) return '#ef4444'
    if (verdict === 'CONFLICTING') return '#f59e0b'
    return '#6b7280'
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <span style={styles.title}>Market Overview</span>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {lastUpdate && <span style={styles.updateTime}>Updated {lastUpdate}</span>}
          <button style={styles.refreshBtn} onClick={fetchData}>↻ Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.empty}>Loading market data...</div>
      ) : !data || data.status === 'no_data' ? (
        <div style={styles.empty}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
          <div>No market data yet</div>
          <div style={{ fontSize: 13, color: '#4b5563', marginTop: 8 }}>
            Data appears after first market scan at 9:30 AM IST
          </div>
        </div>
      ) : (
        <div style={styles.content}>

          {/* Market Breadth */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Market Breadth</div>
            <div style={styles.breadthCard}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{
                  fontSize: 28, fontWeight: 800,
                  color: biasColor(data.breadth?.bias || 'MIXED'),
                }}>
                  {data.breadth?.bias === 'BULLISH' ? '▲ BULLISH BIAS' :
                   data.breadth?.bias === 'BEARISH' ? '▼ BEARISH BIAS' :
                   '⇄ MIXED'}
                </div>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  {data.total_signals || 0} signals from latest scan
                </div>
              </div>

              {/* Breadth bar */}
              <div style={{ marginBottom: 16 }}>
                <div style={{
                  display: 'flex', height: 12, borderRadius: 6,
                  overflow: 'hidden', gap: 2,
                }}>
                  <div style={{
                    width: `${((data.breadth?.bullish || 0) / (data.total_signals || 1)) * 100}%`,
                    background: '#22c55e', borderRadius: '6px 0 0 6px',
                    minWidth: (data.breadth?.bullish || 0) > 0 ? 4 : 0,
                  }} />
                  <div style={{
                    width: `${((data.breadth?.conflicting || 0) / (data.total_signals || 1)) * 100}%`,
                    background: '#f59e0b',
                    minWidth: (data.breadth?.conflicting || 0) > 0 ? 4 : 0,
                  }} />
                  <div style={{
                    width: `${((data.breadth?.monitor || 0) / (data.total_signals || 1)) * 100}%`,
                    background: '#374151',
                    minWidth: (data.breadth?.monitor || 0) > 0 ? 4 : 0,
                  }} />
                  <div style={{
                    width: `${((data.breadth?.bearish || 0) / (data.total_signals || 1)) * 100}%`,
                    background: '#ef4444', borderRadius: '0 6px 6px 0',
                    minWidth: (data.breadth?.bearish || 0) > 0 ? 4 : 0,
                  }} />
                </div>
              </div>

              {/* Breadth counts */}
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                {[
                  { label: 'Bullish', value: data.breadth?.bullish || 0, color: '#22c55e' },
                  { label: 'Conflicting', value: data.breadth?.conflicting || 0, color: '#f59e0b' },
                  { label: 'Monitor', value: data.breadth?.monitor || 0, color: '#4b5563' },
                  { label: 'Bearish', value: data.breadth?.bearish || 0, color: '#ef4444' },
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>
                      {item.value}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Two column layout */}
          <div style={styles.twoCol}>
            {/* Institutional Buying */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                🟢 Institutional Buying
              </div>
              {(data.institutional_buying?.length || 0) === 0 ? (
                <div style={styles.emptySection}>No institutional buying detected</div>
              ) : (
                <div style={styles.flowTable}>
                  <div style={styles.tableHeader}>
                    <span style={styles.th}>Symbol</span>
                    <span style={styles.th}>Spot</span>
                    <span style={styles.th}>Conviction</span>
                    <span style={styles.th}>Score</span>
                  </div>
                  {(data.institutional_buying || []).map(s => (
                    <div key={s.symbol} style={styles.tableRow}>
                      <span style={{ ...styles.td, color: '#22c55e', fontWeight: 700 }}>
                        {s.symbol}
                      </span>
                      <span style={styles.td}>₹{s.spot.toLocaleString('en-IN')}</span>
                      <span style={{
                        ...styles.td, fontSize: 11, fontWeight: 700,
                        color: s.conviction === 'HIGH' ? '#22c55e' :
                               s.conviction === 'FRESH' ? '#00d4ff' : '#6b7280',
                      }}>
                        {s.conviction}
                      </span>
                      <span style={{ ...styles.td, color: '#00d4ff' }}>
                        {Math.min(s.score, 10)}/10
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Institutional Selling */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                🔴 Institutional Selling
              </div>
              {(data.institutional_selling?.length || 0) === 0 ? (
                <div style={styles.emptySection}>No institutional selling detected</div>
              ) : (
                <div style={styles.flowTable}>
                  <div style={styles.tableHeader}>
                    <span style={styles.th}>Symbol</span>
                    <span style={styles.th}>Spot</span>
                    <span style={styles.th}>Conviction</span>
                    <span style={styles.th}>Score</span>
                  </div>
                  {(data.institutional_selling || []).map(s => (
                    <div key={s.symbol} style={styles.tableRow}>
                      <span style={{ ...styles.td, color: '#ef4444', fontWeight: 700 }}>
                        {s.symbol}
                      </span>
                      <span style={styles.td}>₹{s.spot.toLocaleString('en-IN')}</span>
                      <span style={{
                        ...styles.td, fontSize: 11, fontWeight: 700,
                        color: s.conviction === 'HIGH' ? '#22c55e' :
                               s.conviction === 'FRESH' ? '#00d4ff' : '#6b7280',
                      }}>
                        {s.conviction}
                      </span>
                      <span style={{ ...styles.td, color: '#00d4ff' }}>
                        {Math.min(s.score, 10)}/10
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      <div style={styles.disclaimer}>
        ⚠️ Research intelligence only. Not SEBI registered investment advice.
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: { background: '#0a0e1a', minHeight: '100vh', color: '#fff' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid #1f2937', background: '#0d1117',
    flexWrap: 'wrap' as const, gap: 8,
  },
  title: { color: '#fff', fontSize: 18, fontWeight: 700 },
  backBtn: {
    background: 'transparent', color: '#6b7280', border: '1px solid #374151',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  refreshBtn: {
    background: 'transparent', color: '#00d4ff', border: '1px solid #00d4ff',
    padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  updateTime: { color: '#4b5563', fontSize: 12 },
  content: { padding: 24 },
  section: { marginBottom: 32 },
  sectionTitle: {
    fontSize: 15, fontWeight: 700, color: '#fff',
    marginBottom: 16, paddingBottom: 8,
    borderBottom: '1px solid #1f2937',
  },
  breadthCard: {
    background: '#111827', border: '1px solid #1f2937',
    borderRadius: 12, padding: 24,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 24,
  },
  flowTable: { background: '#111827', borderRadius: 12, overflow: 'hidden' },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '1fr 100px 90px 60px',
    padding: '10px 16px', background: '#1f2937', gap: 8,
  },
  th: { color: '#6b7280', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const },
  tableRow: {
    display: 'grid', gridTemplateColumns: '1fr 100px 90px 60px',
    padding: '12px 16px', borderTop: '1px solid #1f2937',
    gap: 8, alignItems: 'center',
  },
  td: { color: '#e5e7eb', fontSize: 13 },
  emptySection: { color: '#4b5563', fontSize: 13, padding: '20px 0' },
  empty: { textAlign: 'center' as const, padding: '80px 20px', color: '#6b7280' },
  disclaimer: {
    textAlign: 'center' as const, padding: 16,
    color: '#374151', fontSize: 11, borderTop: '1px solid #1f2937',
  },
}
